import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ModalProvider } from "@/contexts/ModalContext";
import { useMessageNotifications } from "@/hooks/useMessageNotifications";
import "@/i18n";
import React, { Suspense } from "react";

import Index from "./pages/Index";
import AuthPage from "./pages/AuthPage";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-loaded routes to reduce initial JS bundle
const HomePage = React.lazy(() => import("./pages/HomePage"));
const DiscoveryPage = React.lazy(() => import("./pages/DiscoveryPage"));
const BusinessProfilePage = React.lazy(() => import("./pages/BusinessProfilePage"));
const BookingsPage = React.lazy(() => import("./pages/BookingsPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const MessagesPage = React.lazy(() => import("./pages/MessagesPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const ConsumerTermsPage = React.lazy(() => import("./pages/ConsumerTermsPage"));
const ConsumerPrivacyPage = React.lazy(() => import("./pages/ConsumerPrivacyPage"));
const BusinessTermsPage = React.lazy(() => import("./pages/BusinessTermsPage"));
const BusinessPrivacyPage = React.lazy(() => import("./pages/BusinessPrivacyPage"));

const queryClient = new QueryClient();

const LazyFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Ladataan...</div>
  </div>
);

const AppRoutes = () => {
  useMessageNotifications();

  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/discover" element={<DiscoveryPage />} />
        <Route path="/business-profile" element={<ProtectedRoute><BusinessProfilePage /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/legal/consumer-terms" element={<ConsumerTermsPage />} />
        <Route path="/legal/consumer-privacy" element={<ConsumerPrivacyPage />} />
        <Route path="/legal/business-terms" element={<BusinessTermsPage />} />
        <Route path="/legal/business-privacy" element={<BusinessPrivacyPage />} />
        <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ModalProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <main>
              <AppRoutes />
            </main>
          </BrowserRouter>
        </TooltipProvider>
      </ModalProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
